import React, { useState } from 'react';
import { MessageCircle, Headphones, Share2, X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

export const FloatingActionButton = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([
    { from: 'support', text: 'Olá! Como podemos ajudar você hoje?' }
  ]);

  const handleSupport = () => {
    setIsChatOpen(true);
    setIsOpen(false);
  };

  const handleSendMessage = () => {
    if (!chatMessage.trim()) return;
    
    setChatMessages(prev => [...prev, { from: 'user', text: chatMessage }]);
    setChatMessage('');
    
    // Simulated response
    setTimeout(() => {
      setChatMessages(prev => [...prev, { 
        from: 'support', 
        text: 'Obrigado pela mensagem! Nossa equipe irá responder em breve. Para atendimento mais rápido, entre em contato pelo WhatsApp.' 
      }]);
    }, 1000);
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      toast.success('Copiado!');
    } catch (err) {
      toast.error('Erro ao copiar link');
    }
    setIsOpen(false);
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/5532984945548?text=Olá! Gostaria de saber mais sobre a Gameodys.', '_blank');
    setIsOpen(false);
  };

  return (
    <div className="fixed bottom-8 right-8 z-50">
      {/* Chat Window */}
      {isChatOpen && (
        <Card className="absolute bottom-20 right-0 w-80 h-96 bg-dark-primary border-purple-primary/30 shadow-2xl animate-slide-up flex flex-col">
          <CardHeader className="pb-2 border-b border-purple-primary/20">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-archive text-white">Suporte</CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsChatOpen(false)}
                className="text-gray-400 hover:text-white h-8 w-8 p-0"
              >
                <X size={18} />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-3 space-y-3">
            {chatMessages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.from === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-lg text-sm ${
                    msg.from === 'user'
                      ? 'bg-green-primary text-white'
                      : 'bg-dark-secondary text-gray-300'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
          </CardContent>
          <div className="p-3 border-t border-purple-primary/20">
            <div className="flex gap-2">
              <Input
                value={chatMessage}
                onChange={(e) => setChatMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Digite sua mensagem..."
                className="flex-1 bg-dark-secondary border-purple-primary/20 text-white placeholder:text-gray-500"
              />
              <Button
                onClick={handleSendMessage}
                className="bg-green-primary hover:bg-green-secondary"
              >
                <Send size={18} />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Options Menu */}
      {isOpen && !isChatOpen && (
        <Card className="absolute bottom-20 right-0 w-64 bg-dark-primary border-purple-primary/30 shadow-2xl animate-slide-up">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-archive text-white">Como podemos ajudar?</CardTitle>
            <CardDescription className="text-sm font-hero text-gray-400">
              Escolha uma opção abaixo
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Button
              onClick={handleSupport}
              className="w-full justify-start bg-dark-secondary hover:bg-purple-primary/20 text-white border border-purple-primary/20 transition-all duration-300"
            >
              <Headphones className="mr-2" size={18} />
              Suporte
            </Button>
            <Button
              onClick={handleWhatsApp}
              className="w-full justify-start bg-dark-secondary hover:bg-green-primary/20 text-white border border-green-primary/20 transition-all duration-300"
            >
              <MessageCircle className="mr-2" size={18} />
              WhatsApp
            </Button>
            <Button
              onClick={handleShare}
              className="w-full justify-start bg-dark-secondary hover:bg-purple-primary/20 text-white border border-purple-primary/20 transition-all duration-300"
            >
              <Share2 className="mr-2" size={18} />
              Compartilhar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Main FAB */}
      <Button
        onClick={() => {
          if (isChatOpen) {
            setIsChatOpen(false);
          } else {
            setIsOpen(!isOpen);
          }
        }}
        size="lg"
        className={`h-16 w-16 rounded-full shadow-2xl transition-all duration-300 ${
          isOpen || isChatOpen
            ? 'bg-red-600 hover:bg-red-700 rotate-90'
            : 'bg-green-primary hover:bg-green-secondary hover:scale-110'
        }`}
      >
        {isOpen || isChatOpen ? <X size={24} /> : <MessageCircle size={24} />}
      </Button>
    </div>
  );
};
